import {
  EmailContentType,
  Locales,
  ScheduleOffsetUnit,
  TicketingFormCategory,
  getDefaultAutoRenewalSubject,
  getDefaultExpiringMembershipSubject,
  getFilenameFromUrl,
} from "@simplyk/common";

import { AutomatedCommunicationsFormData, ReminderFormData } from "../types";

import { EventReminderFallback, buildEventReminderFallback } from "./eventReminderFallback";
import {
  getDefaultOrderShippedBody,
  getDefaultOrderShippedSubject,
  getDefaultReadyForPickupBody,
  getDefaultReadyForPickupSubject,
} from "./fulfillmentEmailDefaults";
import { getDefaultAutoRenewalBody, getDefaultExpiringBody } from "./membershipEmailDefaults";

import { Translate } from "@/common/hooks/useTranslate";
import { TicketingOutput } from "@/common/types/trpc";
import {
  EditorEmailContentType,
  FormEmailContents,
  getEditorEmailText,
  getTicketingThankYouContentType,
} from "@/features/LiveFormEditor/common/formEmailContents";
import { Translations } from "@/public/@types/translations";

type DashboardKey = keyof Translations["dashboard"] & string;

type LocalizedField = NonNullable<TicketingOutput["ticketingFields"]>[number] | undefined;

type ReminderFromDb =
  | {
      subject?: string | null;
      body?: string | null;
      attachmentUrl?: string | null;
      scheduleOffset?: number | null;
      scheduleOffsetUnit?: ScheduleOffsetUnit | null;
    }
  | null
  | undefined;

interface BuildDefaultValuesParams {
  apiTicketing: TicketingOutput;
  localizedField: LocalizedField;
  remindersData: { reminder1?: ReminderFromDb; reminder2?: ReminderFromDb } | undefined;
  emailContents: FormEmailContents;
  orgName: string;
  userName: string;
  locale: Locales;
  t: Translate;
}

export const buildDefaultValues = ({
  apiTicketing,
  localizedField,
  remindersData,
  emailContents,
  orgName,
  userName,
  locale,
  t,
}: BuildDefaultValuesParams): AutomatedCommunicationsFormData => {
  const isEvent = apiTicketing.formCategory === TicketingFormCategory.Event;
  const isAuction = apiTicketing.formCategory === TicketingFormCategory.Auction;
  const eventName = localizedField?.title ?? "";
  const hasDate = Boolean(apiTicketing.recurrences?.length);
  const textOf = (type: EditorEmailContentType) => getEditorEmailText(emailContents, type, locale);
  const thankYou = textOf(getTicketingThankYouContentType(isAuction));
  const bidPlaced = isAuction ? textOf(EmailContentType.THANK_YOU) : undefined;
  const nonWinner = textOf(EmailContentType.AUCTION_NON_WINNERS);
  const expiring = textOf(EmailContentType.MEMBERSHIP_EXPIRATION);
  const autoRenewal = textOf(EmailContentType.MEMBERSHIP_AUTO_RENEWAL);
  const readyForPickup = textOf(EmailContentType.READY_FOR_PICKUP);
  const orderShipped = textOf(EmailContentType.ORDER_SHIPPED);
  return {
    thankYouWinner: {
      subject: thankYou.subject ?? "",
      content: thankYou.body ?? "",
      attachment: buildAttachment(thankYou.attachmentUrl),
    },
    thankYouBidPlaced: {
      subject: bidPlaced?.subject ?? "",
      content: bidPlaced?.body ?? "",
    },
    thankYouNonWinner: {
      subject: nonWinner.subject ?? "",
      content: nonWinner.body ?? "",
    },
    outbid: { smsEnabled: Boolean(apiTicketing.outbidSmsEnabled) },
    reminder1: buildReminderDefaults({
      reminderFromDb: remindersData?.reminder1,
      apiTicketingEmailEnabled: apiTicketing.reminder1EmailEnabled,
      apiTicketingSmsEnabled: apiTicketing.reminder1SmsEnabled,
      isEvent,
      eventDefault: buildEventReminderFallback({ reminderNumber: 1, eventName, hasDate, t }),
      auctionDefaultSubjectKey: "ticketing.automatedCommunications.reminder1.defaultSubject",
      auctionDefaultContentKey: "ticketing.automatedCommunications.reminder1.defaultContent",
      eventDefaultOffset: 7,
      auctionDefaultOffsetUnit: ScheduleOffsetUnit.Days,
      t,
    }),
    reminder2: buildReminderDefaults({
      reminderFromDb: remindersData?.reminder2,
      apiTicketingEmailEnabled: apiTicketing.reminder2EmailEnabled,
      apiTicketingSmsEnabled: apiTicketing.reminder2SmsEnabled,
      isEvent,
      eventDefault: buildEventReminderFallback({ reminderNumber: 2, eventName, hasDate, t }),
      auctionDefaultSubjectKey: "ticketing.automatedCommunications.reminder2.defaultSubject",
      auctionDefaultContentKey: "ticketing.automatedCommunications.reminder2.defaultContent",
      eventDefaultOffset: 1,
      auctionDefaultOffsetUnit: ScheduleOffsetUnit.Hours,
      t,
    }),
    expiringMembership: {
      enabled: apiTicketing.expiringMembershipEmailEnabled ?? true,
      delayDays: apiTicketing.expiringMembershipEmailDelayDays ?? 30,
      subject: expiring.subject || getDefaultExpiringSubject(orgName, locale),
      content: expiring.body || getDefaultExpiringBody(orgName, userName, locale, t),
    },
    autoRenewal: {
      enabled: apiTicketing.autoRenewalEmailEnabled ?? true,
      delayDays: apiTicketing.autoRenewalEmailDelayDays ?? 30,
      subject: autoRenewal.subject || getDefaultAutoRenewalSubject(locale),
      content: autoRenewal.body || getDefaultAutoRenewalBody(orgName, userName, locale, t),
    },
    readyForPickup: {
      enabled: apiTicketing.readyForPickupEmailEnabled ?? false,
      subject: readyForPickup.subject || getDefaultReadyForPickupSubject(locale),
      content: readyForPickup.body || getDefaultReadyForPickupBody(orgName, locale, t),
    },
    orderShipped: {
      enabled: apiTicketing.orderShippedEmailEnabled ?? false,
      subject: orderShipped.subject || getDefaultOrderShippedSubject(locale),
      content: orderShipped.body || getDefaultOrderShippedBody(orgName, locale, t),
    },
  };
};

interface BuildReminderDefaultsParams {
  reminderFromDb: ReminderFromDb;
  apiTicketingEmailEnabled: boolean | null | undefined;
  apiTicketingSmsEnabled: boolean | null | undefined;
  isEvent: boolean;
  eventDefault: EventReminderFallback;
  auctionDefaultSubjectKey: DashboardKey;
  auctionDefaultContentKey: DashboardKey;
  eventDefaultOffset: number;
  auctionDefaultOffsetUnit: ScheduleOffsetUnit;
  t: Translate;
}

const buildReminderDefaults = ({
  reminderFromDb,
  apiTicketingEmailEnabled,
  apiTicketingSmsEnabled,
  isEvent,
  eventDefault,
  auctionDefaultSubjectKey,
  auctionDefaultContentKey,
  eventDefaultOffset,
  auctionDefaultOffsetUnit,
  t,
}: BuildReminderDefaultsParams): ReminderFormData => {
  const attachment = buildAttachment(reminderFromDb?.attachmentUrl);
  if (isEvent) {
    return {
      enabled: Boolean(
        reminderFromDb && reminderFromDb.scheduleOffset !== null && (reminderFromDb.scheduleOffset ?? 0) > 0
      ),
      emailEnabled: false,
      smsEnabled: false,
      subject: reminderFromDb?.subject || eventDefault.subject,
      content: reminderFromDb?.body || eventDefault.content,
      attachment,
      offset: reminderFromDb?.scheduleOffset || eventDefaultOffset,
      offsetUnit: reminderFromDb?.scheduleOffsetUnit || ScheduleOffsetUnit.Days,
    };
  }
  return {
    enabled: Boolean(apiTicketingEmailEnabled || apiTicketingSmsEnabled),
    emailEnabled: Boolean(apiTicketingEmailEnabled),
    smsEnabled: Boolean(apiTicketingSmsEnabled),
    subject: reminderFromDb?.subject || t("dashboard", auctionDefaultSubjectKey),
    content: reminderFromDb?.body || t("dashboard", auctionDefaultContentKey),
    attachment,
    offset: reminderFromDb?.scheduleOffset ?? 1,
    offsetUnit: reminderFromDb?.scheduleOffsetUnit ?? auctionDefaultOffsetUnit,
  };
};

const buildAttachment = (attachmentUrl: string | null | undefined) => ({
  fileUrl: attachmentUrl || null,
  filename: getFilenameFromUrl(attachmentUrl) || null,
});

const getDefaultExpiringSubject = (orgName: string, locale: Locales): string =>
  getDefaultExpiringMembershipSubject(orgName, locale);
